import { getStorage, setStorage } from '@getjust/commons';
import posthogClient from 'posthog-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIsOrderProcessing } from '$src/hooks/client/useIsOrderProcessing';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useCart } from '$src/hooks/queries';

const EXIT_INTENT_SHOWN_KEY = 'exit_intent_shown';

// PostHog Survey Configuration
// Survey: "Exit reason" - API type survey
// The survey ID is required to identify the survey
// Question ID and text are fetched dynamically from PostHog
// See: https://posthog.com/docs/surveys/implementing-custom-surveys
const POSTHOG_SURVEY_ID = process.env.NEXT_PUBLIC_POSTHOG_SURVEY_ID;

// Module-level cache for fetched survey data
// This avoids re-fetching on every hook call
interface SurveyData {
  questionId: string;
  questionText: string;
}

type SurveyFetchStatus = 'idle' | 'loading' | 'success' | 'error';

let cachedSurveyData: SurveyData | null = null;
let surveyFetchStatus: SurveyFetchStatus = 'idle';
let surveyFetchPromise: Promise<SurveyData | null> | null = null;

// Subscribers to notify when survey fetch completes
const fetchStatusSubscribers = new Set<() => void>();

const notifySubscribers = () => {
  fetchStatusSubscribers.forEach((callback) => callback());
};

// Fetch survey question data from PostHog
// Uses PostHog's getSurveys API to get the actual question ID and text
// Returns null if survey cannot be fetched (exit intent will not be shown)
// See: https://posthog.com/docs/surveys/implementing-custom-surveys#fetching-surveys-manually
const fetchSurveyData = (): Promise<SurveyData | null> => {
  // Return cached data if already fetched
  if (cachedSurveyData) {
    return Promise.resolve(cachedSurveyData);
  }

  // Return existing promise if fetch is in progress
  if (surveyFetchPromise) {
    return surveyFetchPromise;
  }

  // Cannot fetch without survey ID
  if (!POSTHOG_SURVEY_ID) {
    surveyFetchStatus = 'error';
    return Promise.resolve(null);
  }

  surveyFetchStatus = 'loading';

  surveyFetchPromise = new Promise((resolve) => {
    try {
      posthogClient.getSurveys((surveys) => {
        const survey = surveys.find((s) => s.id === POSTHOG_SURVEY_ID);
        // The PostHog API returns questions with 'id' field, but TypeScript types may not reflect this
        const firstQuestion = survey?.questions?.[0] as { id?: string; question?: string } | undefined;

        if (firstQuestion?.id && firstQuestion?.question) {
          cachedSurveyData = {
            questionId: firstQuestion.id,
            questionText: firstQuestion.question,
          };
          surveyFetchStatus = 'success';
          notifySubscribers();
          resolve(cachedSurveyData);
        } else {
          // Survey or question not found - don't show exit intent
          surveyFetchStatus = 'error';
          notifySubscribers();
          resolve(null);
        }
      });
    } catch {
      surveyFetchStatus = 'error';
      notifySubscribers();
      resolve(null);
    }
  });

  return surveyFetchPromise;
};

// Check if survey data was successfully fetched
const isSurveyReady = (): boolean => {
  return surveyFetchStatus === 'success' && cachedSurveyData !== null;
};

// Build the survey questions array for PostHog
// This is required for getSurveyResponse to work as expected
const getSurveyQuestions = () => {
  if (!cachedSurveyData) {
    return undefined;
  }
  return [
    {
      id: cachedSurveyData.questionId,
      question: cachedSurveyData.questionText,
    },
  ];
};

// Build the response properties object
// Uses $survey_response_<question_id> format
// IMPORTANT: For multiple_choice type surveys, PostHog expects an ARRAY of selected values
// See: https://posthog.com/docs/surveys/implementing-custom-surveys#response-formats
const getResponseProperties = (reason: string) => {
  if (!cachedSurveyData) {
    return {};
  }
  // For multiple choice surveys, response must be an array
  return {
    [`$survey_response_${cachedSurveyData.questionId}`]: [reason],
  };
};

// These match the PostHog survey choices exactly
export type ExitIntentReason =
  | 'Still shopping'
  | 'Looking for a promo code'
  | "Shipping doesn't work for me"
  | "I'm having a technical issue"
  | 'Other';

export const EXIT_INTENT_REASONS: ExitIntentReason[] = [
  'Still shopping',
  'Looking for a promo code',
  "Shipping doesn't work for me",
  "I'm having a technical issue",
  'Other',
];

interface UseExitIntentOptions {
  /** Whether the exit intent feature is enabled (via feature flag). Defaults to true. */
  isFeatureEnabled?: boolean;
  /** Whether to shuffle the options (except "Other" which stays last). Defaults to false. */
  shuffleOptions?: boolean;
}

interface UseExitIntentReturn {
  isOpen: boolean;
  triggerExitIntent: () => boolean;
  onLeave: () => void;
  onReasonSelected: (reason: ExitIntentReason) => Promise<void>;
  pendingExitCallback: (() => void) | null;
  setPendingExitCallback: (callback: (() => void) | null) => void;
  /** Whether to shuffle the options (except "Other" which stays last) */
  shuffleOptions: boolean;
}

export function useExitIntent(options: UseExitIntentOptions = {}): UseExitIntentReturn {
  const { isFeatureEnabled = true, shuffleOptions = false } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [pendingExitCallback, setPendingExitCallback] = useState<(() => void) | null>(null);
  const [surveyReady, setSurveyReady] = useState(() => isSurveyReady());
  const isOrderProcessing = useIsOrderProcessing();
  const { data: cart } = useCart();
  const { track } = useTrackAnalytics();

  // Track sessionStorage state with a state variable so we can update it
  const [sessionShown, setSessionShown] = useState(() => {
    if (typeof window === 'undefined') return true;
    return getStorage<boolean>('sessionStorage', EXIT_INTENT_SHOWN_KEY) === true;
  });

  const hasBeenShownThisSession = sessionShown;

  // Fetch survey data from PostHog on mount
  // Exit intent will only be shown if survey is successfully fetched
  useEffect(() => {
    if (!POSTHOG_SURVEY_ID) {
      return;
    }

    // If already ready, no need to fetch
    if (isSurveyReady()) {
      setSurveyReady(true);
      return;
    }

    // Subscribe to status changes
    const handleStatusChange = () => {
      setSurveyReady(isSurveyReady());
    };
    fetchStatusSubscribers.add(handleStatusChange);

    // Trigger fetch
    fetchSurveyData();

    return () => {
      fetchStatusSubscribers.delete(handleStatusChange);
    };
  }, []);

  const canShowExitIntent = useMemo(() => {
    // Don't show if:
    // - Feature is disabled
    // - Order is processing
    // - Already shown this session
    // - Survey data not successfully fetched from PostHog
    return isFeatureEnabled && !isOrderProcessing && !hasBeenShownThisSession && surveyReady;
  }, [isFeatureEnabled, isOrderProcessing, hasBeenShownThisSession, surveyReady]);

  const markAsShown = useCallback(() => {
    setStorage('sessionStorage', EXIT_INTENT_SHOWN_KEY, true);
    setSessionShown(true);
  }, []);

  const openPopup = useCallback(() => {
    if (!canShowExitIntent) {
      return false;
    }

    markAsShown();
    setIsOpen(true);

    // Track with our analytics
    track(AnalyticsEventName.EXIT_INTENT_POPUP_SHOWN, {
      totalAmount: cart?.totalAmount,
      cartCurrency: cart?.currency,
    });

    // Track PostHog survey shown event
    // See: https://posthog.com/docs/surveys/implementing-custom-surveys
    posthogClient.capture('survey shown', {
      $survey_id: POSTHOG_SURVEY_ID,
      $survey_questions: getSurveyQuestions(),
    });

    return true;
  }, [canShowExitIntent, markAsShown, track, cart?.totalAmount, cart?.currency]);

  /**
   * User chose to leave without selecting a reason.
   * Closes the popup, tracks dismissal, and executes the pending exit callback.
   */
  const onLeave = useCallback(() => {
    setIsOpen(false);

    // Track with our analytics
    track(AnalyticsEventName.EXIT_INTENT_DISMISSED, {
      totalAmount: cart?.totalAmount,
      cartCurrency: cart?.currency,
    });

    // Track PostHog survey dismissed event
    posthogClient.capture('survey dismissed', {
      $survey_id: POSTHOG_SURVEY_ID,
      $survey_questions: getSurveyQuestions(),
    });

    // Execute the pending exit callback (let user leave)
    if (pendingExitCallback) {
      pendingExitCallback();
      setPendingExitCallback(null);
    }
  }, [track, cart?.totalAmount, cart?.currency, pendingExitCallback]);

  /**
   * User selected a reason - immediately submit and let them leave.
   * No separate submit button needed.
   * Uses sendBeacon transport for reliable delivery before navigation.
   */
  const onReasonSelected = useCallback(
    async (reason: ExitIntentReason) => {
      // Track with our analytics
      track(AnalyticsEventName.EXIT_INTENT_REASON_SELECTED, {
        reason,
        totalAmount: cart?.totalAmount,
        cartCurrency: cart?.currency,
      });

      // Track PostHog survey sent event with the response
      // Use sendBeacon transport for reliable delivery before navigation
      // $survey_questions is required for getSurveyResponse to work as expected
      // See: https://posthog.com/docs/surveys/implementing-custom-surveys
      posthogClient.capture(
        'survey sent',
        {
          $survey_id: POSTHOG_SURVEY_ID,
          $survey_questions: getSurveyQuestions(),
          ...getResponseProperties(reason),
        },
        { transport: 'sendBeacon' },
      );

      // Close popup immediately for better UX
      setIsOpen(false);

      // Execute the pending exit callback (let user leave)
      // sendBeacon ensures the event is queued for delivery even during navigation
      if (pendingExitCallback) {
        pendingExitCallback();
        setPendingExitCallback(null);
      }
    },
    [track, cart?.totalAmount, cart?.currency, pendingExitCallback],
  );

  /**
   * Manually trigger exit intent (e.g., from back button click).
   * Returns true if popup was shown, false if conditions weren't met.
   */
  const triggerExitIntent = useCallback(() => openPopup(), [openPopup]);

  // Use a ref to store the latest openPopup function to avoid re-registering
  // the handler every time cart data changes (which would reset the library state)
  const openPopupRef = useRef(openPopup);
  useEffect(() => {
    openPopupRef.current = openPopup;
  }, [openPopup]);

  return {
    isOpen,
    triggerExitIntent,
    onLeave,
    onReasonSelected,
    pendingExitCallback,
    setPendingExitCallback,
    shuffleOptions,
  };
}
